import { useEffect, useRef } from 'react';

type GoCarousel = (direction: string) => void;

export const useSnapArrows = (
  container: string,
  child: string,
): { goCarousel: GoCarousel } => {
  const carouselPositionsRef = useRef<number[][]>([]);
  const halfContainerRef = useRef(0);
  const currentItemRef = useRef(0);

  const containerSelector = `#${container}`;
  const childSelector = `${containerSelector} ${child}`;

  const getContainer = (): HTMLElement | null => {
    return document.querySelector<HTMLElement>(containerSelector);
  };

  const getChildren = (): HTMLElement[] => {
    return Array.from(document.querySelectorAll<HTMLElement>(childSelector));
  };

  const getCarouselPositions = (): void => {
    carouselPositionsRef.current = getChildren().map((li) => [
      li.offsetLeft,
      li.offsetLeft + li.offsetWidth,
    ]);

    const offsetWidth = getContainer()?.offsetWidth || 0;
    halfContainerRef.current = offsetWidth / 2;
  };

  useEffect(() => {
    getCarouselPositions();
  }, []);

  const goCarousel = (direction: string): void => {
    const container = getContainer();
    if (!container) {
      return;
    }

    const currentScrollLeft = container.scrollLeft;
    const currentScrollRight = currentScrollLeft + container.offsetWidth;

    const carouselPositions = carouselPositionsRef.current;

    if (currentScrollLeft === 0 && direction === 'next') {
      currentItemRef.current = 1;
    } else if (
      currentScrollRight === container.scrollWidth &&
      direction === 'previous'
    ) {
      // --- probably needs to be dynamic
      currentItemRef.current = carouselPositions.length - 4;
    } else {
      const currentMiddlePosition =
        currentScrollLeft + halfContainerRef.current;

      for (let i = 0; i < carouselPositions.length; i++) {
        const [itemStart, itemEnd] = carouselPositions[i];

        const isMiddleItem =
          currentMiddlePosition >= itemStart &&
          currentMiddlePosition <= itemEnd;

        if (isMiddleItem) {
          // the current item is the item to the left of the middle item
          currentItemRef.current = i - 1;

          if (direction === 'next') {
            currentItemRef.current++;
          } else if (direction === 'previous') {
            currentItemRef.current--;
          }
        }
      }
    }

    const currentItem = currentItemRef.current;
    container.scrollTo({
      left: currentItem < 0 ? 0 : carouselPositions[currentItem][0],
      behavior: 'smooth',
    });
  };

  return { goCarousel };
};
